// src/store/actions/encuestas.js
import api from '../../axios-encuestas';
import * as actionTypes from './actionTypes';

// --------------------
// Creators básicos
// --------------------
export const cambiarEstadoPagina = (estado) => ({
  type: actionTypes.CAMBIAR_ESTADO_PAGINA,
  estado
});

export const actualizarHashEncuesta = (hash) => ({
  type: actionTypes.ACTUALIZAR_HASH_ENCUESTA,
  hash
});

export const guardarDatosEncuesta = (datos) => ({
  type: actionTypes.GUARDAR_DATOS_ENCUESTA,
  datos
});

export const guardarContenidoEnvio = (contenido) => ({
  type: 'GUARDAR_CONTENIDO_ENVIO',
  contenido
});

export const guardarEstadoEnvio = (estado) => ({
  type: actionTypes.GUARDAR_ESTADO_ENVIO,
  estado
});

// --------------------
// GET encuesta por hash (Axios parsea JSON automáticamente)
// --------------------
export const obtenerDatosEncuesta = (hash) => {
  return async (dispatch) => {
    dispatch(cambiarEstadoPagina(0)); // cargando
    try {
      const { data: jsonData } = await api.get('encuestas', { params: { hash } });
      dispatch(guardarDatosEncuesta(jsonData));
      dispatch(cambiarEstadoPagina(1)); // cargado
    } catch (error) {
      const status = error?.response?.status;
      const raw = error?.response?.data;
      const texto = typeof raw === 'string' ? raw : raw?.detail || raw?.message || JSON.stringify(raw || {});
      const msg = status ? `Error ${status} - ${texto}` : error?.message || 'Error de red';

      dispatch(guardarDatosEncuesta({ error: msg }));
      dispatch(cambiarEstadoPagina(2)); // error
    }
  };
};

// --------------------
// POST/PUT respuestas (con fallback 400 -> PUT)
// --------------------
// Cada paso del formulario manda todas las respuestas acumuladas hasta ese momento. El alta se
// hace con POST; a partir del segundo envío la API responde 400 "ya se encuentra respondida" y
// se reemplaza por un PUT.
//
// OJO: la API de develop (develop.calidad.api.jalisco365.com.ar) está desactualizada y NO tiene
// la ruta PUT — responde 405 Method Not Allowed con `Allow: POST`. En producción sí existe y
// devuelve 200. Si aparece un 405 acá, el problema es el entorno, no este código.

// Aplana el cuerpo de una respuesta de error a texto buscable, sin importar en qué campo venga
// el mensaje (title, detail, errors, message, o el body directamente como string).
const cuerpoComoTexto = (body) => {
  if (typeof body === 'string') return body;
  try {
    return JSON.stringify(body ?? '');
  } catch {
    return '';
  }
};

export const enviarRespuestasEncuesta = (data, { silencioso = false } = {}) => {
  return async (dispatch) => {
    // función común para POST/PUT
    const enviar = (method) =>
      api.request({
        url: 'encuestas/Respuesta',
        method,
        data
      });

    // El error visible tiene que ser el del intento actual, no el de uno anterior
    dispatch(guardarEstadoEnvio({ envioFallido: 0, detalleEnvio: '' }));

    try {
      try {
        await enviar('POST');
      } catch (errPost) {
        const status = errPost?.response?.status;
        const body = errPost?.response?.data;

        let title = (typeof body === 'object' && (body?.title || body?.error)) || 'Error en el envío';
        let detalle = (() => {
          if (typeof body === 'string') return body;
          if (body?.errors) return Object.values(body.errors).flat().join('\n');
          return body?.detail || body?.message || '';
        })();

        const detalleStr = (detalle || '').toString();

        // La encuesta ya tiene respuestas guardadas (la primera se manda sola al abrir el link
        // del mail): el alta se reemplaza por una actualización.
        const yaRespondida =
          status === 400 && cuerpoComoTexto(body).toLowerCase().includes('ya se encuentra respondida');

        if (yaRespondida) {
          await enviar('PUT');
        } else {
          const mensajeError = `Error ${status ?? ''} - ${title}\n${detalleStr}`.trim();
          if (silencioso) {
            dispatch(guardarEstadoEnvio({ autosaveError: 1, detalleEnvio: mensajeError }));
            return;
          }
          dispatch(guardarEstadoEnvio({ envioFallido: 1, detalleEnvio: mensajeError }));
          throw new Error(mensajeError);
        }
      }

      // 204: Axios deja data = '' -> no necesitamos hacer nada
      if (!silencioso) {
        dispatch(cambiarEstadoPagina(3)); // mostrar “enviado”
      } else {
        dispatch(guardarEstadoEnvio({ autosaveOk: 1, autosaveAt: Date.now() }));
      }
    } catch (error) {
      const mensaje = error?.message || String(error);
      if (!silencioso) {
        dispatch(guardarEstadoEnvio({ envioFallido: 1, detalleEnvio: mensaje }));
        dispatch(cambiarEstadoPagina(4));
      } else {
        dispatch(guardarEstadoEnvio({ autosaveError: 1, detalleEnvio: mensaje }));
      }
    }
  };
};

// --------------------
// Helper puro (reutilizable/testeable)
// --------------------
// Saca la opción elegida y el valor del `contenido` que emiten los controles
const opcionYValor = (c) => {
  let opcionId = null;
  let valor = '';

  if (Array.isArray(c)) {
    // Caso 1: [id, texto]
    if (!Array.isArray(c[0])) {
      opcionId = Number.parseInt(c[0], 10);
      valor = String(c[1] ?? '');
    } else {
      // Caso 2: [[id, label]]  (Multiselect con selección única)
      const par = c[0] || [];
      opcionId = Number.parseInt(par[0], 10);
      valor = String(par[1] ?? '');
    }
  }

  return { opcionId, valor };
};

export const armarPayloadEncuesta = (respuestas, datosCrudos, hashEncuesta) => {
  const respuestasArray = Object.values(respuestas || {});

  const resultado = respuestasArray
    .filter((contenido) => !contenido?.esAdicional)
    .map((contenido) => {
      const { opcionId, valor } = opcionYValor(contenido?.contenido);
      return {
        encuestaPreguntaId: contenido?.numeroPregunta,
        encuestaPreguntaOpcionId: opcionId,
        valor
      };
    });

  // Las adicionales van aparte. En texto libre el control manda [numeroPregunta, texto]: no hay opción.
  const resultadoAdicionales = respuestasArray
    .filter((contenido) => contenido?.esAdicional)
    .map((contenido) => {
      const { opcionId, valor } = opcionYValor(contenido?.contenido);
      return {
        encuestaPreguntaId: contenido.encuestaPreguntaId,
        preguntaAdicionalId: contenido.preguntaAdicionalId,
        preguntaAdicionalOpcionId: contenido.tipoControlId === 3 || Number.isNaN(opcionId) ? 0 : (opcionId ?? 0),
        valor
      };
    });

  // Nombre y apellido desde cadena "usuarioCliente"
  const usuarioRaw = (datosCrudos?.soporte?.usuarioCliente || '').trim();
  const partes = usuarioRaw.split(' ').filter(Boolean);
  const nombre = partes[0] || '';
  const apellido = partes.length > 1 ? partes.slice(1).join(' ') : '';

  return {
    hash: hashEncuesta || '000-0000-0000-0000-000000000000',
    encuestaId: datosCrudos?.encuesta?.id,
    nombreUsuario: nombre,
    apellidoUsuario: apellido,
    respuestasPreguntas: resultado,
    respuestasAdicionales: resultadoAdicionales
  };
};

// --------------------
// Orquestador que pasa la opción {silencioso}
// --------------------
export const enviarEncuesta = (respuestas, datosCrudos, hashEncuesta, { silencioso = false } = {}) => {
  return async (dispatch) => {
    const data = armarPayloadEncuesta(respuestas, datosCrudos, hashEncuesta);
    dispatch(guardarContenidoEnvio(data));
    await dispatch(enviarRespuestasEncuesta(data, { silencioso }));
  };
};
